import { useEffect, useState } from 'preact/hooks';
import { api } from '../../api/client';
import type {
    AccountDto,
    ClaimDto,
    ClaimInboxItemDto,
    DiscoveryMode,
} from '../../api/types';
import { ErrorTile, Eyebrow } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { useTranslation } from '../../i18n';

/**
 * Claims section of /me/account. Two parts:
 *
 *   • Suggestions inbox - pending ClaimSuggestion rows the Mode-B
 *     gwtag-scan surfaced. Accept promotes to a confirmed AuthorClaim.
 *     Decline closes the suggestion. Suppress silently drops every
 *     future detection for the same (provider, handle).
 *   • Active claims - confirmed AuthorClaim rows. Revoke soft-deletes
 *     a row so the catalog's unclaimed-creator view goes back to
 *     stale-author rendering.
 */
export function AccountClaimsPage() {
    const t = useTranslation();
    const [inbox, setInbox] = useState<ClaimInboxItemDto[] | null>(null);
    const [claims, setClaims] = useState<ClaimDto[] | null>(null);
    const [account, setAccount] = useState<AccountDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [savingMode, setSavingMode] = useState(false);
    const toast = useToast();

    const refresh = async () => {
        try {
            const [i, c, a] = await Promise.all([
                api.me.claimsInbox.list(),
                api.me.claims.list(),
                api.me.account.get(),
            ]);
            setInbox(i);
            setClaims(c);
            setAccount(a);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    useEffect(() => {
        void refresh();
    }, []);

    const onChangeMode = async (mode: DiscoveryMode) => {
        if (savingMode || !account || account.discoveryMode === mode) return;
        setSavingMode(true);
        try {
            const next = await api.me.account.setDiscoveryMode(mode);
            setAccount(next);
            toast.push('success', t('me.claims.discovery.saved', { mode }));
        } catch (e) {
            toast.push('error', t('me.claims.discovery.saveError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setSavingMode(false);
        }
    };

    const onInboxAction = async (
        item: ClaimInboxItemDto,
        action: 'accept' | 'decline' | 'suppress',
    ) => {
        setBusyId(item.id);
        try {
            if (action === 'accept') await api.me.claimsInbox.accept(item.id);
            if (action === 'decline') await api.me.claimsInbox.decline(item.id);
            if (action === 'suppress') await api.me.claimsInbox.suppress(item.id);
            await refresh();
            const actionLabel =
                action === 'accept'
                    ? t('me.claims.inbox.accepted')
                    : action === 'decline'
                      ? t('me.claims.inbox.declined')
                      : t('me.claims.inbox.suppressed');
            toast.push(
                'success',
                <>
                    {actionLabel}{' '}
                    {item.providerSlug}/{item.providerHandle}.
                </>,
            );
        } catch (e) {
            toast.push('error', t('me.claims.inbox.actionError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusyId(null);
        }
    };

    const onRevoke = async (claim: ClaimDto) => {
        setBusyId(claim.id);
        try {
            await api.me.claims.revoke(claim.id);
            await refresh();
            toast.push(
                'success',
                <>
                    {t('me.claims.active.revoked')} {claim.providerSlug}/{claim.providerHandle}.
                </>,
            );
        } catch (e) {
            toast.push('error', t('me.claims.active.revokeError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <AccountShell
            title={t('me.claims.title')}
            subtitle={t('me.claims.subtitle')}
        >
            <div class="mx-auto w-full max-w-3xl space-y-10 px-[clamp(20px,3.5vw,56px)] py-10">
                {error && <ErrorTile title={t('me.claims.loadError')} details={error} />}

                {account && (
                    <DiscoverySettings
                        current={account.discoveryMode}
                        busy={savingMode}
                        onChange={(m) => void onChangeMode(m)}
                    />
                )}

                <section>
                    <Eyebrow class="mb-3">
                        {t('me.claims.inbox.eyebrow')}{' '}
                        <span class="text-fg-faint">
                            ({inbox?.length ?? 0})
                        </span>
                    </Eyebrow>
                    {inbox === null ? (
                        <p class="text-sm text-fg-muted">{t('me.claims.loading')}</p>
                    ) : inbox.length === 0 ? (
                        <p class="text-sm text-fg-muted">
                            {t('me.claims.inbox.empty')}
                        </p>
                    ) : (
                        <ul class="space-y-3">
                            {inbox.map((item) => (
                                <li
                                    key={item.id}
                                    class="rounded border border-rule bg-surface-raised p-3"
                                >
                                    <div class="flex flex-wrap items-baseline gap-2">
                                        <span class="font-mono text-[12px] uppercase tracking-[0.14em] text-fg-muted">
                                            {item.providerSlug}
                                        </span>
                                        <span class="font-medium text-fg-primary">
                                            {item.providerHandle}
                                        </span>
                                        <span class="ml-auto text-xs text-fg-muted">
                                            {t('me.claims.inbox.detected')}{' '}
                                            {new Date(
                                                item.detectedAt,
                                            ).toLocaleString()}
                                        </span>
                                    </div>
                                    <p class="mt-1 text-xs text-fg-muted">
                                        {item.workCount} {item.workCount === 1 ? t('me.claims.inbox.work') : t('me.claims.inbox.works')}
                                        {item.detectedInField &&
                                            ` · ${t('me.claims.inbox.foundIn')} ${item.detectedInField}`}
                                    </p>
                                    {item.detectionUrl && (
                                        <a
                                            href={item.detectionUrl}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            class="mt-1 inline-block text-xs text-accent hover:text-fg-primary"
                                        >
                                            {t('me.claims.inbox.openUrl')}
                                        </a>
                                    )}
                                    <div class="mt-3 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => onInboxAction(item, 'accept')}
                                            disabled={busyId !== null}
                                            class="rounded border border-accent bg-accent px-3 py-1 text-xs font-medium text-surface-base transition-colors hover:bg-fg-primary disabled:opacity-50"
                                        >
                                            {t('me.claims.inbox.acceptCta')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onInboxAction(item, 'decline')}
                                            disabled={busyId !== null}
                                            class="rounded border border-rule-strong px-3 py-1 text-xs text-fg-primary transition-colors hover:border-accent disabled:opacity-50"
                                        >
                                            {t('me.claims.inbox.declineCta')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onInboxAction(item, 'suppress')}
                                            disabled={busyId !== null}
                                            class="rounded border border-rule-strong px-3 py-1 text-xs text-fg-muted transition-colors hover:border-state-failed hover:text-state-failed disabled:opacity-50"
                                            title={t('me.claims.inbox.suppressTitle')}
                                        >
                                            {t('me.claims.inbox.suppressCta')}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section>
                    <Eyebrow class="mb-3">
                        {t('me.claims.active.eyebrow')}{' '}
                        <span class="text-fg-faint">
                            ({claims?.length ?? 0})
                        </span>
                    </Eyebrow>
                    {claims === null ? (
                        <p class="text-sm text-fg-muted">{t('me.claims.loading')}</p>
                    ) : claims.length === 0 ? (
                        <p class="text-sm text-fg-muted">
                            {t('me.claims.active.empty')}
                        </p>
                    ) : (
                        <ul class="space-y-2">
                            {claims.map((claim) => (
                                <li
                                    key={claim.id}
                                    class="flex items-center justify-between gap-3 rounded border border-rule bg-surface-raised px-3 py-2"
                                >
                                    <div class="min-w-0">
                                        <div class="font-mono text-[12px] uppercase tracking-[0.14em] text-fg-muted">
                                            {claim.providerSlug}
                                        </div>
                                        <div class="truncate font-medium text-fg-primary">
                                            {claim.providerHandle}
                                        </div>
                                        <div class="text-xs text-fg-muted">
                                            {t('me.claims.active.claimed')}{' '}
                                            {new Date(
                                                claim.claimedAt,
                                            ).toLocaleString()}
                                            {claim.validationMethod &&
                                                ` · ${t('me.claims.active.via')} ${claim.validationMethod}`}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => onRevoke(claim)}
                                        disabled={busyId !== null}
                                        class="rounded border border-rule-strong px-2 py-1 text-xs text-fg-muted transition-colors hover:border-state-failed hover:text-state-failed disabled:opacity-50"
                                    >
                                        {t('me.claims.active.revokeCta')}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </AccountShell>
    );
}

function DiscoverySettings({
    current,
    busy,
    onChange,
}: {
    current: DiscoveryMode;
    busy: boolean;
    onChange: (mode: DiscoveryMode) => void;
}) {
    const t = useTranslation();

    const discoveryOptions: { mode: DiscoveryMode; label: string; hint: string }[] = [
        {
            mode: 'Auto',
            label: t('me.claims.discovery.auto'),
            hint: t('me.claims.discovery.autoHint'),
        },
        {
            mode: 'Suggest',
            label: t('me.claims.discovery.suggest'),
            hint: t('me.claims.discovery.suggestHint'),
        },
        {
            mode: 'Off',
            label: t('me.claims.discovery.off'),
            hint: t('me.claims.discovery.offHint'),
        },
    ];

    return (
        <section class="rounded border border-rule bg-surface-raised p-4">
            <Eyebrow class="mb-2">{t('me.claims.discovery.eyebrow')}</Eyebrow>
            <p class="mb-3 text-xs text-fg-muted">
                {t('me.claims.discovery.body')}
            </p>
            <div class="flex flex-wrap gap-2">
                {discoveryOptions.map((opt) => {
                    const active = opt.mode === current;
                    return (
                        <button
                            key={opt.mode}
                            type="button"
                            onClick={() => onChange(opt.mode)}
                            disabled={busy || active}
                            title={opt.hint}
                            class={`rounded border px-3 py-1.5 text-xs transition-colors ${
                                active
                                    ? 'cursor-default border-accent bg-accent-glow text-accent'
                                    : 'border-rule-strong text-fg-muted hover:border-accent hover:text-fg-primary disabled:opacity-50'
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </section>
    );
}

